/**
 * Page FAQ - Foire Aux Questions (FrontOffice)
 * L'API FAQ nécessite d'être connecté : les visiteurs anonymes sont invités à se connecter.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { faqApi, FAQCategory, FAQItem, ROLE_LABELS, useAuth } from '@e-store/shared';
import { AmazonHeader } from '../components/AmazonHeader';
import './FAQPage.css';

export function FAQPage() {
  const { isAuthenticated, user } = useAuth();

  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [items, setItems] = useState<FAQItem[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoriesRes, itemsRes] = await Promise.all([
        faqApi.getCategories(),
        faqApi.getItems(), // Items publiés seulement pour les non-admins
      ]);

      setCategories(categoriesRes.data.categories);
      setItems(itemsRes.data.items);
    } catch (err) {
      console.error('Erreur chargement FAQ:', err);
      setError('Erreur lors du chargement de la FAQ');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    if (selectedCategoryId && item.categoryId !== selectedCategoryId) return false;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const toggleItem = async (faqId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
      try {
        await faqApi.incrementView(faqId);
      } catch (err) {
        console.error('Erreur incrémentation vues:', err);
      }
    }
    setExpandedItems(newExpanded);
  };

  const handleFeedback = async (faqId: string, helpful: boolean) => {
    if (feedbackGiven.has(faqId)) return;
    try {
      await faqApi.recordFeedback(faqId, helpful);
      setFeedbackGiven(new Set(feedbackGiven).add(faqId));
    } catch (err) {
      console.error('Erreur enregistrement feedback:', err);
    }
  };

  /** L'action décrite est-elle hors du rôle de l'utilisateur connecté ? */
  const isLockedForUser = (item: FAQItem): boolean => {
    if (!item.allowedRoles || item.allowedRoles.length === 0) return false;
    if (!user) return false;
    return !item.allowedRoles.includes(user.role);
  };

  const allowedRolesLabel = (item: FAQItem): string =>
    (item.allowedRoles ?? []).map((role) => ROLE_LABELS[role] ?? role).join(', ');

  return (
    <div className="fo-faq-page">
      <AmazonHeader />

      <div className="fo-faq-container">
        <div className="fo-faq-header">
          <h1>❓ Foire Aux Questions</h1>
          <p>Trouvez rapidement des réponses à vos questions</p>
        </div>

        {!isAuthenticated ? (
          <div className="fo-faq-login-invite">
            <p>Connectez-vous pour consulter la FAQ et poser vos questions.</p>
            <Link to="/login?redirect=/faq" className="fo-faq-login-btn">
              Se connecter
            </Link>
          </div>
        ) : loading ? (
          <div className="fo-faq-loading">Chargement de la FAQ...</div>
        ) : error ? (
          <div className="fo-faq-error">{error}</div>
        ) : (
          <>
            {/* Recherche */}
            <div className="fo-faq-search">
              <input
                type="text"
                placeholder="🔍 Rechercher une question..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filtres par catégorie */}
            <div className="fo-faq-categories">
              <button
                className={`fo-faq-category-btn ${selectedCategoryId === null ? 'active' : ''}`}
                onClick={() => setSelectedCategoryId(null)}
              >
                Toutes
              </button>
              {categories.map((category) => (
                <button
                  key={category.categoryId}
                  className={`fo-faq-category-btn ${
                    selectedCategoryId === category.categoryId ? 'active' : ''
                  }`}
                  onClick={() => setSelectedCategoryId(category.categoryId)}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Items */}
            <div className="fo-faq-items">
              {filteredItems.length === 0 ? (
                <div className="fo-faq-empty">Aucune question trouvée.</div>
              ) : (
                filteredItems.map((item) => (
                  <div
                    key={item.faqId}
                    className={`fo-faq-item ${expandedItems.has(item.faqId) ? 'expanded' : ''}`}
                  >
                    <button className="fo-faq-question" onClick={() => toggleItem(item.faqId)}>
                      <span>
                        {item.question}
                        {isLockedForUser(item) && (
                          <span
                            className="fo-faq-role-badge"
                            title={`Action réservée à : ${allowedRolesLabel(item)}`}
                          >
                            🔒 {allowedRolesLabel(item)}
                          </span>
                        )}
                      </span>
                      <span className="fo-faq-expand-icon">
                        {expandedItems.has(item.faqId) ? '▼' : '▶'}
                      </span>
                    </button>

                    {expandedItems.has(item.faqId) && (
                      <div className="fo-faq-answer">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: item.answer.replace(/\n/g, '<br />'),
                          }}
                        />
                        <div className="fo-faq-feedback">
                          <span>Cette réponse est-elle utile ?</span>
                          {feedbackGiven.has(item.faqId) ? (
                            <span className="fo-faq-thanks">Merci pour votre retour !</span>
                          ) : (
                            <div className="fo-faq-feedback-btns">
                              <button onClick={() => handleFeedback(item.faqId, true)}>
                                👍 Oui
                              </button>
                              <button onClick={() => handleFeedback(item.faqId, false)}>
                                👎 Non
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
